import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus,
    Clock,
    Video,
    AlertCircle,
    Link as LinkIcon,
    ArrowLeft,
    MonitorIcon,
    CalendarDays,
    FileVideo,
    Users,
    Globe,
    BellRing,
    Sparkles,
    Check
} from 'lucide-react';

interface Cohort {
    id: string;
    name: string;
    course_id?: number;
    course?: {
        id: number;
        title: string;
    };
}

interface Course {
    id: number;
    title: string;
}

const WEEKDAYS = [
    { key: 'monday', label: 'Mon', full: 'Monday' },
    { key: 'tuesday', label: 'Tue', full: 'Tuesday' },
    { key: 'wednesday', label: 'Wed', full: 'Wednesday' },
    { key: 'thursday', label: 'Thu', full: 'Thursday' },
    { key: 'friday', label: 'Fri', full: 'Friday' },
    { key: 'saturday', label: 'Sat', full: 'Saturday' },
    { key: 'sunday', label: 'Sun', full: 'Sunday' },
];

const DURATION_OPTIONS = [
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
    { value: 150, label: '2.5 hours' },
    { value: 180, label: '3 hours' },
    { value: 240, label: '4 hours' },
];

export default function CreateLiveSession() {
    const navigate = useNavigate();
    const [cohorts, setCohorts] = useState<Cohort[]>([]);
    const [courses, setCourses] = useState<Course[]>([]);
    const [loadingData, setLoadingData] = useState(true);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        cohortId: '',
        courseId: '',
        selectedDays: ['saturday'] as string[],
        startTime: '01:00',
        durationMinutes: 120,
        hasEndDate: false,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        meetingLink: '',
    });

    const [recordingUrl, setRecordingUrl] = useState<string>('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                const [cohortsRes, coursesRes] = await Promise.all([
                    fetch(`${API_URL}/cohorts`, {
                        headers: {
                            'Accept': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    }),
                    fetch(`${API_URL}/courses`, {
                        headers: {
                            'Accept': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    })
                ]);

                if (cohortsRes.ok) {
                    const cohortsData = await cohortsRes.json();
                    if (Array.isArray(cohortsData)) {
                        setCohorts(cohortsData);
                    }
                }

                if (coursesRes.ok) {
                    const coursesData = await coursesRes.json();
                    if (Array.isArray(coursesData)) {
                        setCourses(coursesData);
                    }
                }
            } catch (err) {
                console.error("Fetch Cohorts/Courses Error:", err);
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [API_URL]);

    const handleCohortChange = (cohortId: string) => {
        const selectedCohort = cohorts.find(c => c.id === cohortId);
        setFormData(prev => ({
            ...prev,
            cohortId,
            courseId: selectedCohort?.course_id ? selectedCohort.course_id.toString() : (selectedCohort?.course?.id ? selectedCohort.course.id.toString() : prev.courseId)
        }));
    };

    const toggleDay = (dayKey: string) => {
        setFormData(prev => {
            const exists = prev.selectedDays.includes(dayKey);
            let updatedDays: string[];
            if (exists) {
                if (prev.selectedDays.length === 1) {
                    return prev; // keep at least one day
                }
                updatedDays = prev.selectedDays.filter(d => d !== dayKey);
            } else {
                updatedDays = [...prev.selectedDays, dayKey];
            }
            return { ...prev, selectedDays: updatedDays };
        });
    };

    // Format human readable summary for preview
    const scheduleSummary = useMemo(() => {
        if (formData.selectedDays.length === 0) return 'No days selected';
        const formattedDays = formData.selectedDays.map(d => {
            const match = WEEKDAYS.find(w => w.key === d);
            return match ? match.full : d;
        });

        let daysText = '';
        if (formattedDays.length === 1) {
            daysText = `Every ${formattedDays[0]}`;
        } else if (formattedDays.length === 2) {
            daysText = `Every ${formattedDays[0]} & ${formattedDays[1]}`;
        } else {
            const last = formattedDays[formattedDays.length - 1];
            const rest = formattedDays.slice(0, -1);
            daysText = `Every ${rest.join(', ')} & ${last}`;
        }

        // Format 24h to 12h AM/PM
        let timeFormatted = formData.startTime;
        if (formData.startTime) {
            const [hours, minutes] = formData.startTime.split(':').map(Number);
            const period = hours >= 12 ? 'PM' : 'AM';
            const adjustedHours = hours % 12 || 12;
            timeFormatted = `${adjustedHours}:${minutes < 10 ? '0' + minutes : minutes} ${period}`;
        }

        const durationMatch = DURATION_OPTIONS.find(d => d.value === Number(formData.durationMinutes));
        const durationText = durationMatch ? durationMatch.label : `${formData.durationMinutes} mins`;

        return `${daysText} at ${timeFormatted} Central Time (${durationText})`;
    }, [formData.selectedDays, formData.startTime, formData.durationMinutes]);

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.title.trim()) errors.title = 'Session title is required';
        if (!formData.cohortId && !formData.courseId) errors.cohortId = 'Please select a cohort or course';
        if (formData.selectedDays.length === 0) errors.days = 'Select at least one recurring day';
        if (!formData.startTime) errors.startTime = 'Start time is required';
        if (!formData.startDate) errors.startDate = 'Schedule start date is required';

        if (formData.hasEndDate) {
            if (!formData.endDate) {
                errors.endDate = 'End date is required when enabled';
            } else if (formData.endDate < formData.startDate) {
                errors.endDate = 'End date cannot be earlier than start date';
            }
        }

        if (formData.meetingLink && !/^https?:\/\/.+/.test(formData.meetingLink)) {
            errors.meetingLink = 'Must be a valid URL starting with http:// or https://';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);

            try {
                const submitData = new FormData();
                submitData.append('title', formData.title.trim());
                if (formData.description) submitData.append('description', formData.description.trim());
                if (formData.cohortId) submitData.append('cohort_id', formData.cohortId);
                if (formData.courseId) submitData.append('course_id', formData.courseId);
                
                submitData.append('is_recurring', '1');
                submitData.append('recurring_days', JSON.stringify(formData.selectedDays));
                submitData.append('start_time', formData.startTime);
                submitData.append('duration_minutes', formData.durationMinutes.toString());
                submitData.append('timezone', 'America/Chicago');
                submitData.append('schedule_start_date', formData.startDate);
                if (formData.hasEndDate && formData.endDate) {
                    submitData.append('schedule_end_date', formData.endDate);
                }
                submitData.append('meeting_link', formData.meetingLink);
                if (recordingUrl) {
                    submitData.append('recording_link', recordingUrl);
                }

                const response = await fetch(`${API_URL}/live-sessions`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: submitData
                });

                if (response.ok) {
                    navigate('/instructor/live', { state: { success: true } });
                } else {
                    const errorData = await response.json();
                    alert(errorData.message || 'Failed to create recurring live session schedule');
                }
            } catch (err) {
                console.error("Create Live Session Error:", err);
                alert('An error occurred. Please check connection and try again.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleFileUpload = async (file: File) => {
        const token = localStorage.getItem('token');
        setUploading(true);
        setUploadProgress(0);

        const uploadFormData = new FormData();
        uploadFormData.append('video', file);

        try {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', `${API_URL}/upload-video`, true);
            xhr.setRequestHeader('Accept', 'application/json');
            if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress(Math.round((event.loaded / event.total) * 100));
                }
            };

            const response: any = await new Promise((resolve, reject) => {
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            resolve(JSON.parse(xhr.responseText));
                        } catch { reject(new Error('Invalid response')); }
                    } else {
                        reject(new Error('Upload failed'));
                    }
                };
                xhr.onerror = () => reject(new Error('Network error'));
                xhr.send(uploadFormData);
            });

            if (response.success) {
                setRecordingUrl(response.video_url);
            } else {
                alert(response.message || 'Upload failed');
            }
        } catch (err: any) {
            alert(err.message || 'An error occurred during upload');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteVideo = async () => {
        if (!window.confirm('Delete this recording permanently?')) return;
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${API_URL}/delete-video`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ video_url: recordingUrl })
            });

            if (response.ok) {
                setRecordingUrl('');
            } else {
                alert('Failed to delete video');
            }
        } catch (err) {
            alert('An error occurred during deletion');
        }
    };

    return (
        <div className="animate-fade-in-up" style={{ maxWidth: '840px', margin: '0 auto', paddingBottom: '3rem' }}>
            <style>{`
                .staff-scope .create-live-card {
                    background: white;
                    border-radius: 24px;
                    padding: 2.5rem;
                    border: 1px solid var(--index-border-color);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
                }

                @media (max-width: 768px) {
                    .staff-scope .create-live-card {
                        padding: 1.5rem;
                        border-radius: 20px;
                    }
                }

                .staff-scope .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.5rem;
                }

                .staff-scope .form-group-modern {
                    margin-bottom: 1.75rem;
                }

                .staff-scope .form-label-modern {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.9rem;
                    font-weight: 700;
                    color: var(--index-text-heading);
                    margin-bottom: 0.65rem;
                }

                .staff-scope .form-input-modern {
                    width: 100%;
                    padding: 0.85rem 1.15rem;
                    border: 2px solid var(--index-hover-bg);
                    border-radius: 14px;
                    background: var(--index-hover-bg);
                    outline: none;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    font-size: 0.95rem;
                    color: var(--index-text-heading);
                }

                .staff-scope .form-input-modern:focus {
                    background: white;
                    border-color: var(--index-primary-color);
                    box-shadow: 0 0 0 4px color-mix(in srgb, var(--index-primary-color) 10%, transparent);
                }

                .staff-scope .form-input-modern.error {
                    border-color: var(--lgl-error, #ef4444);
                    background: var(--index-hover-bg);
                }

                .staff-scope .error-text {
                    color: var(--lgl-error, #ef4444);
                    font-size: 0.8rem;
                    font-weight: 600;
                    margin-top: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.35rem;
                }

                .staff-scope .btn-submit-live {
                    background: var(--lgl-charcoal, #0F5132);
                    color: white;
                    width: 100%;
                    padding: 1.15rem;
                    border-radius: 16px;
                    border: none;
                    font-size: 1rem;
                    font-weight: 800;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    transition: all 0.3s;
                    margin-top: 1rem;
                    box-shadow: 0 10px 20px color-mix(in srgb, var(--lgl-charcoal, #0F5132) 20%, transparent);
                }

                .staff-scope .btn-submit-live:hover:not(:disabled) {
                    background: var(--index-text-heading, #064e3b);
                    transform: translateY(-2px);
                    box-shadow: 0 15px 30px color-mix(in srgb, var(--lgl-charcoal, #0F5132) 30%, transparent);
                }

                .staff-scope .btn-submit-live:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .staff-scope .back-btn {
                    background: var(--index-hover-bg);
                    border: none;
                    padding: 0.75rem;
                    border-radius: 14px;
                    cursor: pointer;
                    color: var(--lgl-charcoal);
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .staff-scope .back-btn:hover {
                    background: var(--index-border-color);
                    color: var(--index-text-heading);
                }

                .staff-scope .weekday-pill-grid {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: 0.5rem;
                }

                @media (max-width: 640px) {
                    .staff-scope .weekday-pill-grid {
                        grid-template-columns: repeat(4, 1fr);
                    }
                }

                .staff-scope .weekday-btn {
                    padding: 0.75rem 0.5rem;
                    border-radius: 12px;
                    border: 2px solid var(--index-border-color);
                    background: white;
                    font-weight: 700;
                    font-size: 0.85rem;
                    cursor: pointer;
                    text-align: center;
                    transition: all 0.2s;
                    color: var(--index-text-secondary);
                }

                .staff-scope .weekday-btn.selected {
                    background: #0F5132;
                    border-color: #0F5132;
                    color: white;
                    box-shadow: 0 4px 12px rgba(15, 81, 50, 0.2);
                }

                .staff-scope .preview-banner {
                    background: linear-gradient(135deg, #0F5132 0%, #064e3b 100%);
                    border-radius: 16px;
                    padding: 1.5rem;
                    color: white;
                    margin-bottom: 2rem;
                }

                .staff-scope .tz-notice-box {
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    border-radius: 12px;
                    padding: 0.85rem 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1.75rem;
                    font-size: 0.85rem;
                    color: #166534;
                    font-weight: 600;
                }

                @media (max-width: 640px) {
                    .staff-scope .form-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>

            <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <button
                    onClick={() => navigate('/instructor/live')}
                    className="back-btn"
                >
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h1 className="dashboard-header-title" style={{ margin: 0, fontSize: '1.75rem', fontWeight: 800 }}>Create Live Session Schedule</h1>
                    <p style={{ color: 'var(--index-text-secondary)', marginTop: '0.25rem', fontSize: '0.95rem' }}>
                        Set up a recurring weekly live class schedule once. The LMS will automatically handle upcoming occurrences and reminders.
                    </p>
                </div>
            </div>

            {/* Timezone Notice Banner */}
            <div className="tz-notice-box">
                <Globe size={20} style={{ color: '#0F5132', flexShrink: 0 }} />
                <div>
                    <strong>Central Time Authoritative:</strong> All Live Sessions are scheduled in <strong>Central Time (America/Chicago)</strong>. Daylight saving time changes (CST/CDT) are calculated automatically.
                </div>
            </div>

            <div className="create-live-card">
                {/* Dynamic Schedule Preview */}
                <div className="preview-banner">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#86efac', fontWeight: 800 }}>
                        <Sparkles size={16} /> Recurring Schedule Summary
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.3, marginBottom: '6px' }}>
                        {formData.title ? formData.title : 'Live Session'}
                    </div>
                    <div style={{ fontSize: '0.95rem', color: '#dcfce7', fontWeight: 600 }}>
                        📅 {scheduleSummary}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#bbf7d0', marginTop: '10px' }}>
                        <BellRing size={14} /> Automated 30-minute email reminders will be sent to enrolled students before each class.
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Session Title */}
                    <div className="form-group-modern">
                        <label className="form-label-modern">
                            <MonitorIcon size={16} /> Session Title
                        </label>
                        <input
                            type="text"
                            className={`form-input-modern ${formErrors.title ? 'error' : ''}`}
                            placeholder="e.g. Project Management Live Class & Workshop"
                            value={formData.title}
                            onChange={e => setFormData({ ...formData, title: e.target.value })}
                        />
                        {formErrors.title && <p className="error-text"><AlertCircle size={14} /> {formErrors.title}</p>}
                    </div>

                    {/* Cohort Selection */}
                    <div className="form-group-modern">
                        <label className="form-label-modern">
                            <Users size={16} /> Select Cohort
                        </label>
                        <select
                            className={`form-input-modern ${formErrors.cohortId ? 'error' : ''}`}
                            value={formData.cohortId}
                            onChange={e => handleCohortChange(e.target.value)}
                            disabled={loadingData}
                        >
                            <option value="">{loadingData ? 'Loading cohorts...' : 'Select a Cohort'}</option>
                            {cohorts.map(cohort => (
                                <option key={cohort.id} value={cohort.id}>
                                    {cohort.name} {cohort.course?.title ? `(${cohort.course.title})` : ''}
                                </option>
                            ))}
                        </select>
                        {formErrors.cohortId && <p className="error-text"><AlertCircle size={14} /> {formErrors.cohortId}</p>}
                    </div>

                    {/* Recurring Weekdays Selector */}
                    <div className="form-group-modern">
                        <label className="form-label-modern">
                            <CalendarDays size={16} /> Recurring Day(s) of Week
                        </label>
                        <div className="weekday-pill-grid">
                            {WEEKDAYS.map(day => {
                                const isSelected = formData.selectedDays.includes(day.key);
                                return (
                                    <button
                                        type="button"
                                        key={day.key}
                                        className={`weekday-btn ${isSelected ? 'selected' : ''}`}
                                        onClick={() => toggleDay(day.key)}
                                    >
                                        {isSelected && <Check size={12} style={{ display: 'inline', marginRight: '3px' }} />}
                                        {day.label}
                                    </button>
                                );
                            })}
                        </div>
                        {formErrors.days && <p className="error-text"><AlertCircle size={14} /> {formErrors.days}</p>}
                    </div>

                    {/* Start Time & Duration */}
                    <div className="form-grid">
                        <div className="form-group-modern">
                            <label className="form-label-modern">
                                <Clock size={16} /> Start Time (Central Time)
                            </label>
                            <input
                                type="time"
                                className={`form-input-modern ${formErrors.startTime ? 'error' : ''}`}
                                value={formData.startTime}
                                onChange={e => setFormData({ ...formData, startTime: e.target.value })}
                            />
                            {formErrors.startTime && <p className="error-text"><AlertCircle size={14} /> {formErrors.startTime}</p>}
                            <p style={{ fontSize: '0.78rem', color: 'var(--index-text-secondary)', marginTop: '0.4rem' }}>
                                America/Chicago time (e.g. 01:00 AM)
                            </p>
                        </div>

                        <div className="form-group-modern">
                            <label className="form-label-modern">
                                <Clock size={16} /> Duration
                            </label>
                            <select
                                className="form-input-modern"
                                value={formData.durationMinutes}
                                onChange={e => setFormData({ ...formData, durationMinutes: Number(e.target.value) })}
                            >
                                {DURATION_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Schedule Start Date & Optional End Date */}
                    <div className="form-grid">
                        <div className="form-group-modern">
                            <label className="form-label-modern">
                                <CalendarDays size={16} /> Schedule Starts
                            </label>
                            <input
                                type="date"
                                className={`form-input-modern ${formErrors.startDate ? 'error' : ''}`}
                                value={formData.startDate}
                                onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                            />
                            {formErrors.startDate && <p className="error-text"><AlertCircle size={14} /> {formErrors.startDate}</p>}
                        </div>

                        <div className="form-group-modern">
                            <label className="form-label-modern" style={{ justifyContent: 'space-between' }}>
                                <span><CalendarDays size={16} /> Schedule Ends (Optional)</span>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 500 }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.hasEndDate}
                                        onChange={e => setFormData({ ...formData, hasEndDate: e.target.checked, endDate: e.target.checked ? formData.endDate : '' })}
                                    />
                                    Has end date
                                </label>
                            </label>
                            <input
                                type="date"
                                className={`form-input-modern ${formErrors.endDate ? 'error' : ''}`}
                                value={formData.endDate}
                                disabled={!formData.hasEndDate}
                                placeholder="Repeats indefinitely"
                                onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                                style={{ opacity: formData.hasEndDate ? 1 : 0.5 }}
                            />
                            {formErrors.endDate && <p className="error-text"><AlertCircle size={14} /> {formErrors.endDate}</p>}
                            {!formData.hasEndDate && (
                                <p style={{ fontSize: '0.78rem', color: 'var(--index-text-secondary)', marginTop: '0.4rem' }}>
                                    Repeats indefinitely until disabled.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Meeting Link */}
                    <div className="form-group-modern">
                        <label className="form-label-modern">
                            <LinkIcon size={16} /> Meeting URL (Zoom / Teams / Meet)
                        </label>
                        <input
                            type="url"
                            className={`form-input-modern ${formErrors.meetingLink ? 'error' : ''}`}
                            placeholder="https://zoom.us/j/... or https://teams.microsoft.com/..."
                            value={formData.meetingLink}
                            onChange={e => setFormData({ ...formData, meetingLink: e.target.value })}
                        />
                        {formErrors.meetingLink && <p className="error-text"><AlertCircle size={14} /> {formErrors.meetingLink}</p>}
                        <p style={{ fontSize: '0.8rem', color: 'var(--index-text-secondary)', marginTop: '0.4rem' }}>
                            Students will receive this link directly in their 30-minute reminder email and on their dashboard.
                        </p>
                    </div>

                    {/* Description */}
                    <div className="form-group-modern">
                        <label className="form-label-modern">
                            <MonitorIcon size={16} /> Description / Agenda (Optional)
                        </label>
                        <textarea
                            rows={3}
                            className="form-input-modern"
                            placeholder="Briefly describe what will be covered in this recurring weekly live class..."
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            style={{ resize: 'vertical' }}
                        />
                    </div>

                    {/* Pre-recorded Session Upload */}
                    <div className="form-group-modern">
                        <label className="form-label-modern">
                            <FileVideo size={16} /> Pre-recorded Video (Optional)
                        </label>
                        {uploading ? (
                            <div style={{ padding: '2rem', background: 'var(--index-hover-bg)', borderRadius: '14px', textAlign: 'center' }}>
                                <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Uploading... {uploadProgress}%</div>
                                <div style={{ width: '100%', height: '8px', background: 'var(--index-border-color)', borderRadius: '4px', overflow: 'hidden' }}>
                                    <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#0F5132', transition: 'width 0.3s' }}></div>
                                </div>
                            </div>
                        ) : recordingUrl ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: '#ecfdf5', padding: '1rem', borderRadius: '14px', border: '1px solid #bbf7d0' }}>
                                <FileVideo size={20} color="#0F5132" />
                                <div style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: '#065f46', wordBreak: 'break-all' }}>{recordingUrl}</div>
                                <button type="button" onClick={handleDeleteVideo} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Remove</button>
                            </div>
                        ) : (
                            <>
                                <input
                                    type="file"
                                    accept="video/*"
                                    className="form-input-modern"
                                    onChange={e => {
                                        if (e.target.files?.[0]) handleFileUpload(e.target.files[0]);
                                        e.target.value = '';
                                    }}
                                    style={{ padding: '0.6rem' }}
                                />
                                <p style={{ fontSize: '0.8rem', color: 'var(--index-text-secondary)', marginTop: '0.4rem' }}>
                                    Optional video file for cohort students to access as recording.
                                </p>
                            </>
                        )}
                    </div>

                    {/* Submit Action */}
                    <button type="submit" className="btn-submit-live" disabled={isSubmitting}>
                        {isSubmitting ? 'Creating Recurring Schedule...' : 'Save & Publish Live Schedule'}
                        {!isSubmitting && <Plus size={20} />}
                    </button>
                </form>
            </div>
        </div>
    );
}
